import React from 'react';
import moment from 'moment-timezone';
import AutoIntl from '@au/core/lib/components/elements/AutoIntl';
import AuInput from '@au/core/lib/components/elements/AuInput';
import AuDropDown from '@au/core/lib/components/elements/AuDropDown';
import AuButton from '@au/core/lib/components/elements/AuButton';
import AuToggle, { TOGGLE_SIZE_SMALL } from '@au/core/lib/components/elements/AuToggle';
import { ENTER_KEY } from '@au/core/lib/constants';

import { history as browserHistory } from "../../../history";
import { wrapActionWithTracking } from "../../../utils/analyticsHelpers";
import { filtersToQueryParams } from '../../utils/filters';
import DefaultList from "../List";
import EmbeddedVehicleLookup from "./EmbeddedVehicleLookup";
import customStyles from "../../../css/components/vehicle_view.module.scss";

const URL_KEY = 'propertyKey';
const URL_VALUE = 'propertyValue';
const SEARCH_SECTION_ID = 'vehicle-search-section';

export const PROPERTY_QUERY_PARAM = 'properties';
export const toPropertyParam = (key, value) => ({ [PROPERTY_QUERY_PARAM]: `${key}:${value}` });

export default class VehicleList extends DefaultList {

  constructor(props) {
    super(props);

    const now = moment();
    const fixedCreateTimeStart = now.clone().subtract(29, 'days').toISOString();
    const fixedCreateTimeEnd = now.toISOString();

    const { key, value } = this.getPropertyFromUrl();
    const appliedProperty = key && value ? { key, value } : null;
    const propertyQueryParams = this.buildPropertyQueryParams(
        appliedProperty, fixedCreateTimeStart, fixedCreateTimeEnd
    );

    this.state = {
      ...this.state,
      propertyKey: key,
      propertyValue: value,
      appliedProperty,
      fixedCreateTimeStart,
      fixedCreateTimeEnd,
      propertyQueryParams,
      sidebarFilterQueryParams: {},
      filterQueryParams: propertyQueryParams,
      searchCollapsed: false,
      pendingCollapse: Boolean(appliedProperty),
      searchToggleKey: 0
    };

    this._loadedCount = 0;
  }

  onListSuccess(resp) {
    super.onListSuccess(resp);

    const items = resp?.items || resp?.data?.items || [];
    const count = items.size ?? items.length ?? 0;
    this._loadedCount = resp?.isFirstPage ? count : this._loadedCount + count;
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate(prevProps, prevState);

    const { pendingCollapse, fetching, showError } = this.state;
    const loadJustFinished = prevState.fetching && !fetching;

    if (!pendingCollapse || !loadJustFinished) return;

    const hasRows = !showError && this._loadedCount > 0;

    this.setState(prev => ({
      pendingCollapse: false,
      searchCollapsed: hasRows ? true : prev.searchCollapsed,
      searchToggleKey: hasRows ? prev.searchToggleKey + 1 : prev.searchToggleKey
    }));
  }

  getPropertyFromUrl() {
    const params = new URLSearchParams(browserHistory.location.search);
    return {
      key: (params.get(URL_KEY) || '').trim(),
      value: (params.get(URL_VALUE) || '').trim()
    };
  }

  syncPropertyToUrl(property) {
    const params = new URLSearchParams(browserHistory.location.search);
    if (property) {
      params.set(URL_KEY, property.key);
      params.set(URL_VALUE, property.value);
    } else {
      params.delete(URL_KEY);
      params.delete(URL_VALUE);
    }
    browserHistory.replace({
      pathname: browserHistory.location.pathname,
      search: params.toString()
    });
  }

  buildPropertyQueryParams(property, start, end) {
    if (!property) return {};
    return {
      ...toPropertyParam(property.key, property.value),
      createTimeStart: start,
      createTimeEnd: end
    };
  }

  hasProperty() {
    return Boolean(this.state.appliedProperty);
  }

  async fetchInitialPages(pagesToLoad) {
    if (!this.hasProperty()) {
      this.setState({ fetching: false, allDataFetched: true });
      return Promise.resolve();
    }
    return super.fetchInitialPages(pagesToLoad);
  }

  getFilteredEntities() {
    if (!this.hasProperty()) {
      return this.props.entities.filter(() => false);
    }
    return super.getFilteredEntities();
  }

  getSupportedPropertyKeys() {
    const { entityDef } = this.props;
    return entityDef?.attributes?.properties?.display?.create?.props?.supportedProperties || [];
  }

  getPropertyKeyOptions() {
    const { propertyKey } = this.state;
    const keys = this.getSupportedPropertyKeys();
    const all = propertyKey && !keys.includes(propertyKey) ? [...keys, propertyKey] : keys;
    return all.map(k => ({ val: k, displayString: k }));
  }

  canApplyProperty() {
    const { propertyKey, propertyValue } = this.state;
    return Boolean((propertyKey || '').trim() && (propertyValue || '').trim());
  }

  onPropertyKeyChange = key => this.setState({ propertyKey: key || '' });
  onPropertyValueChange = e => this.setState({ propertyValue: e.target.value });

  onPropertyValueKeyDown = e => {
    if (e.key === ENTER_KEY) {
      this.applyProperty();
    }
  };


  onSearchToggleChange = e =>
      this.setState({ searchCollapsed: !e.target.checked, pendingCollapse: false });

  applyProperty = () => {
    if (!this.canApplyProperty()) return;

    this._loadedCount = 0;

    const key = this.state.propertyKey.trim();
    const value = this.state.propertyValue.trim();
    const { fixedCreateTimeStart, fixedCreateTimeEnd } = this.state;

    const next = { key, value };
    const propertyQueryParams = this.buildPropertyQueryParams(next, fixedCreateTimeStart, fixedCreateTimeEnd);

    this.setState(prev => ({
      appliedProperty: next,
      propertyQueryParams,
      filterQueryParams: { ...prev.sidebarFilterQueryParams, ...propertyQueryParams },
      pendingCollapse: true
    }), () => {
      this.syncPropertyToUrl(next);
      this.applyFilters();
    });
  };

  initializeFilters(filters, filtersCount) {
    const sidebarFilterQueryParams = filtersToQueryParams(filters);
    this.setState({
      sidebarFilterQueryParams,
      filterQueryParams: { ...sidebarFilterQueryParams, ...this.state.propertyQueryParams },
      filtersCount
    }, () => {
      this.syncPropertyToUrl(this.state.appliedProperty);
      this._initFilters.resolve();
    });
  }

  handleFiltersChange(filters, filtersCount) {
    const sidebarFilterQueryParams = filtersToQueryParams(filters);
    this.setState({
      sidebarFilterQueryParams,
      filterQueryParams: { ...sidebarFilterQueryParams, ...this.state.propertyQueryParams },
      filtersCount
    }, () => {
      this.syncPropertyToUrl(this.state.appliedProperty);
      this.applyFilters();
    });
  }

  getUpdaterClassName() {
    return this.state.searchCollapsed
        ? customStyles.vehicle_updater_offset_collapsed
        : customStyles.vehicle_updater_offset;
  }

  renderPropertyInputs() {
    const { propertyKey, propertyValue } = this.state;
    return (
      <div className={customStyles.property_selector}>
        <label className={customStyles.property_label}>
          <AutoIntl displayId="au.entity.attr.property" />
        </label>
        <AuDropDown
          className={customStyles.property_key_dropdown}
          options={this.getPropertyKeyOptions()}
          selection={propertyKey || undefined}
          selectOption={this.onPropertyKeyChange}
          placeholderId="au.vehicles.properties.namePlaceholder"
          />
        <AuInput
          type="text"
          name="propertyValue"
          className={customStyles.property_input}
          value={propertyValue}
          placeholderId="au.vehicles.properties.valuePlaceholder"
          onChange={this.onPropertyValueChange}
          onKeyDown={this.onPropertyValueKeyDown}
          onClear={() => this.onPropertyValueChange({ target: { value: '' } })}
          />
        <AuButton
          type="primary"
          displayId="au.vehicles.property.apply"
          className={customStyles.property_apply_btn}
          disabled={!this.canApplyProperty()}
          onClick={this.applyProperty}
          />
      </div>
    );
  }

  renderSearchSummary() {
    const { appliedProperty, searchCollapsed, searchToggleKey } = this.state;
    return (
      <div className={customStyles.search_summary}>
        <span className={customStyles.search_summary_pill}>
          <AutoIntl displayId="au.entity.attr.property" />: {appliedProperty.key} = {appliedProperty.value}
        </span>
        <div className={customStyles.search_toggle}>
          <AutoIntl
            displayId="au.vehicles.search.options"
            tag="span"
            className={customStyles.search_toggle_caption}
            />
          <AuToggle
            key={searchToggleKey}
            name="vehicleSearchToggle"
            size={TOGGLE_SIZE_SMALL}
            checked={!searchCollapsed}
            onChange={this.onSearchToggleChange}
            />
        </div>
      </div>
    );
  }

  renderContent() {
    const { appliedProperty, searchCollapsed } = this.state;
    return (
      <>
        {appliedProperty && this.renderSearchSummary()}
        <div
          id={SEARCH_SECTION_ID}
          className={searchCollapsed ? customStyles.search_section_collapsed : undefined}
          >
          <EmbeddedVehicleLookup
            endpoint={this.props.endpoint}
            entityDef={this.props.entityDef}
            match={this.props.match}
            controlsClassName={customStyles.vehicle_lookup_controls}
            >
            <div className={customStyles.or_divider}>
              <hr className={customStyles.or_divider_line} />
              <AutoIntl displayId="au.vehicles.or" tag="span" className={customStyles.or_divider_text} />
              <hr className={customStyles.or_divider_line} />
            </div>
            <AutoIntl
              displayId="au.vehicles.propertyBanner"
              tag="p"
              className={customStyles.vehicle_property_hint}
            />
            {this.renderPropertyInputs()}
          </EmbeddedVehicleLookup>
        </div>
        {super.renderContent()}
      </>
    );
  }

  getRowActions({ id, canWrite, permissions, ...otherProps }) {
    const actions = super.getRowActions({ id, canWrite, permissions, ...otherProps });

    const manageBindingAction = {
      key: 'manage_binding_btn',
      displayId: 'au.entity.manageBindings',
      onClick: () => browserHistory.push(this.baseUrl + `/${id}/manage-bindings`)
    };
    actions.splice(2, 0, wrapActionWithTracking(manageBindingAction, 'Vehicle', 'List'));

    return actions;
  }
}
